import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Box,
  Divider,
  Button,
  Tooltip,
  Collapse,
} from '@mui/material';
import {
  Menu as MenuIcon,
  ArrowBack as BackIcon,
  Inventory as ProductsIcon,
  Category as CategoryIcon,
  Straighten as UnitsIcon,
  Dashboard as OverviewIcon,
  Input as EntriesIcon,
  Output as ExitsIcon,
  AssignmentReturn as ReturnIcon,
  Assessment as InventoryIcon,
  People as ClientsIcon,
  LocalShipping as SuppliersIcon,
  Settings as ConfigIcon,
  Warehouse as StockIcon,
  ShoppingCart as AchatsIcon,
  RequestPage as DemandeIcon,
  Receipt as CommandeIcon,
  PersonOutline as DemandeurIcon,
  ExpandLess,
  ExpandMore,
} from '@mui/icons-material';
import { usePermissions } from '../../contexts/PermissionContext';
import ThemeToggle from '../../components/ThemeToggle';
import NotificationsBell from '../../components/NotificationsBell';
import HeaderChatButton from '../../components/HeaderChatButton';

const drawerWidth = 280;
const collapsedDrawerWidth = 64;

const StockLayout: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktopOpen, setDesktopOpen] = useState(true);
  const [achatsOpen, setAchatsOpen] = useState(false);
  const [stockOpen, setStockOpen] = useState(true);
  const [configOpen, setConfigOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission } = usePermissions();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleDesktopDrawerToggle = () => {
    setDesktopOpen(!desktopOpen);
  };

  const handleBackToMain = () => {
    navigate('/dashboard');
  };

  const dashboardItem = {
    text: 'Tableau de bord',
    icon: <OverviewIcon />,
    path: '/stock',
    exact: true,
    miniModuleName: 'Stock Entry',
  };

  const achatsMenuItems = [
    {
      text: 'Demandes d\'achat',
      icon: <DemandeIcon />,
      path: '/stock/demandes-achat',
      miniModuleName: 'Demandes Achat',
    },
    {
      text: 'Bons de commande',
      icon: <CommandeIcon />,
      path: '/stock/bons-commande',
      miniModuleName: 'Bons Commande',
    },
  ];

  const stockMenuItems = [
    {
      text: 'Entrées de Stock',
      icon: <EntriesIcon />,
      path: '/stock/entries',
      miniModuleName: 'Stock Entry',
    },
    {
      text: 'Sorties de Stock',
      icon: <ExitsIcon />,
      path: '/stock/exits',
      miniModuleName: 'Stock Exit',
    },
    {
      text: 'Retours de Stock',
      icon: <ReturnIcon />,
      path: '/stock/returns',
      miniModuleName: 'Stock Return',
    },
    {
      text: 'Inventaire',
      icon: <InventoryIcon />,
      path: '/stock/inventory',
      miniModuleName: 'Inventory',
    },
    {
      text: 'Suivi des Clients',
      icon: <ClientsIcon />,
      path: '/stock/clients',
      miniModuleName: 'Client Tracking',
    },
    {
      text: 'Rapports',
      icon: <UnitsIcon />,
      path: '/stock/reports',
      miniModuleName: null,
      comingSoon: true
    },
  ];

  const configMenuItems = [
    {
      text: 'Gestion des Articles',
      icon: <ProductsIcon />,
      path: '/stock/articles',
      miniModuleName: 'Articles',
    },
    {
      text: 'Gestion des Fournisseurs',
      icon: <SuppliersIcon />,
      path: '/stock/suppliers',
      miniModuleName: 'Suppliers',
    },
    {
      text: 'Gestion des Catégories',
      icon: <CategoryIcon />,
      path: '/stock/categories',
      miniModuleName: 'Categories',
    },
    {
      text: 'Gestion des Unités',
      icon: <UnitsIcon />,
      path: '/stock/units',
      miniModuleName: 'Units',
    },
    {
      text: 'Gestion des Demandeurs',
      icon: <DemandeurIcon />,
      path: '/stock/demandeurs',
      miniModuleName: 'Demandeurs',
    },
  ];

  // Check permissions for each menu item (but show all items)
  const achatsItemsWithPermissions = achatsMenuItems.map((item) => {
    const hasAccess = hasPermission('Stock', 'view', item.miniModuleName);
    return { ...item, hasAccess };
  });

  const menuItemsWithPermissions = stockMenuItems.map((item) => {
    if (item.comingSoon) return { ...item, hasAccess: false }; // Coming soon items are disabled
    if (!item.miniModuleName) return { ...item, hasAccess: true }; // No permission check needed
    const hasAccess = hasPermission('Stock', 'view', item.miniModuleName);
    return { ...item, hasAccess };
  });

  const configItemsWithPermissions = configMenuItems.map((item) => {
    const hasAccess = hasPermission('Config', 'view', item.miniModuleName);
    return { ...item, hasAccess };
  });

  const isActiveRoute = (path: string, exact = false) => {
    if (exact) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  const dashboardHasAccess = hasPermission('Stock', 'view', dashboardItem.miniModuleName);
  const isDashboardActive = dashboardHasAccess && isActiveRoute(dashboardItem.path, dashboardItem.exact);

  const drawer = (
    <div>
      <Toolbar>
        {desktopOpen ? (
          <Button
            fullWidth
            startIcon={<BackIcon />}
            onClick={handleBackToMain}
            variant="outlined"
            size="small"
            sx={{ whiteSpace: 'nowrap', overflow: 'hidden', justifyContent: 'flex-start' }}
          >
            Retour au Menu Principal
          </Button>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
            <IconButton
              onClick={handleBackToMain}
              size="small"
            >
              <BackIcon />
            </IconButton>
          </Box>
        )}
      </Toolbar>
      <Divider />

      <List>
        {/* Dashboard Item */}
        <ListItem disablePadding>
          <ListItemButton
            selected={isDashboardActive}
            onClick={dashboardHasAccess ? () => navigate(dashboardItem.path) : undefined}
            disabled={!dashboardHasAccess}
            sx={{
              minHeight: 48,
              justifyContent: desktopOpen ? 'initial' : 'center',
              px: desktopOpen ? 2.5 : 0,
              '&.Mui-selected': {
                backgroundColor: 'primary.light',
                color: 'primary.contrastText',
                '&:hover': {
                  backgroundColor: 'primary.main',
                },
              },
            }}
          >
            <ListItemIcon sx={{
              minWidth: 0,
              mr: desktopOpen ? 3 : 0,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              color: isDashboardActive ? 'primary.contrastText' : 'inherit',
            }}>
              {dashboardItem.icon}
            </ListItemIcon>
            {desktopOpen && (
              <ListItemText
                primary={dashboardItem.text}
                primaryTypographyProps={{ fontWeight: isDashboardActive ? 600 : 400 }}
              />
            )}
          </ListItemButton>
        </ListItem>

        {/* Gestion des Achats - Collapsible */}
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => setAchatsOpen(!achatsOpen)}
            sx={{
              minHeight: 48,
              justifyContent: desktopOpen ? 'initial' : 'center',
              px: desktopOpen ? 2.5 : 0,
            }}
          >
            <ListItemIcon sx={{
              minWidth: 0,
              mr: desktopOpen ? 3 : 0,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}>
              <AchatsIcon />
            </ListItemIcon>
            {desktopOpen && (
              <>
                <ListItemText
                  primary="Gestion des Achats"
                  primaryTypographyProps={{ fontWeight: 600 }}
                />
                {achatsOpen ? <ExpandLess /> : <ExpandMore />}
              </>
            )}
          </ListItemButton>
        </ListItem>

        <Collapse in={achatsOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {achatsItemsWithPermissions.map((item) => {
              const isDisabled = !item.hasAccess;
              const isActive = !isDisabled && isActiveRoute(item.path);
              const tooltipTitle = !item.hasAccess ? 'Accès refusé' : '';

              return (
                <ListItem key={item.text} disablePadding>
                  <Tooltip title={tooltipTitle} placement="right" arrow>
                    <span style={{ width: '100%' }}>
                      <ListItemButton
                        selected={isActive}
                        onClick={!isDisabled ? () => navigate(item.path) : undefined}
                        disabled={isDisabled}
                        sx={{
                          minHeight: 44,
                          justifyContent: desktopOpen ? 'initial' : 'center',
                          pl: desktopOpen ? 4 : 0,
                          pr: desktopOpen ? 2.5 : 0,
                          '&.Mui-selected': {
                            backgroundColor: 'primary.light',
                            color: 'primary.contrastText',
                            '&:hover': {
                              backgroundColor: 'primary.main',
                            },
                          },
                          '&.Mui-disabled': {
                            opacity: 0.6,
                            cursor: 'not-allowed',
                          },
                          cursor: !isDisabled ? 'pointer' : 'not-allowed',
                        }}
                      >
                        <ListItemIcon sx={{
                          minWidth: 0,
                          mr: desktopOpen ? 3 : 0,
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          color: isActive ? 'primary.contrastText' : 'inherit',
                        }}>
                          {item.icon}
                        </ListItemIcon>
                        {desktopOpen && (
                          <ListItemText
                            primary={item.text}
                            primaryTypographyProps={{
                              fontWeight: isActive ? 600 : 400,
                              fontSize: '0.9rem',
                            }}
                          />
                        )}
                      </ListItemButton>
                    </span>
                  </Tooltip>
                </ListItem>
              );
            })}
          </List>
        </Collapse>

        {/* Gestion Stock - Collapsible */}
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => setStockOpen(!stockOpen)}
            sx={{
              minHeight: 48,
              justifyContent: desktopOpen ? 'initial' : 'center',
              px: desktopOpen ? 2.5 : 0,
            }}
          >
            <ListItemIcon sx={{
              minWidth: 0,
              mr: desktopOpen ? 3 : 0,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}>
              <StockIcon />
            </ListItemIcon>
            {desktopOpen && (
              <>
                <ListItemText
                  primary="Gestion Stock"
                  primaryTypographyProps={{ fontWeight: 600 }}
                />
                {stockOpen ? <ExpandLess /> : <ExpandMore />}
              </>
            )}
          </ListItemButton>
        </ListItem>

        <Collapse in={stockOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {menuItemsWithPermissions.map((item) => {
              const isDisabled = item.comingSoon || !item.hasAccess;
              const isActive = !isDisabled && isActiveRoute(item.path);
              const tooltipTitle = !item.hasAccess && !item.comingSoon ? 'Accès refusé' : '';

              return (
                <ListItem key={item.text} disablePadding>
                  <Tooltip title={tooltipTitle} placement="right" arrow>
                    <span style={{ width: '100%' }}>
                      <ListItemButton
                        selected={isActive}
                        onClick={!isDisabled ? () => navigate(item.path) : undefined}
                        disabled={isDisabled}
                        sx={{
                          minHeight: 44,
                          justifyContent: desktopOpen ? 'initial' : 'center',
                          pl: desktopOpen ? 4 : 0,
                          pr: desktopOpen ? 2.5 : 0,
                          '&.Mui-selected': {
                            backgroundColor: 'primary.light',
                            color: 'primary.contrastText',
                            '&:hover': {
                              backgroundColor: 'primary.main',
                            },
                          },
                          '&.Mui-disabled': {
                            opacity: 0.6,
                            cursor: 'not-allowed',
                          },
                          cursor: !isDisabled ? 'pointer' : 'not-allowed',
                        }}
                      >
                        <ListItemIcon sx={{
                          minWidth: 0,
                          mr: desktopOpen ? 3 : 0,
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          color: isActive ? 'primary.contrastText' : 'inherit',
                        }}>
                          {item.icon}
                        </ListItemIcon>
                        {desktopOpen && (
                          <ListItemText
                            primary={item.text}
                            secondary={item.comingSoon ? 'Bientôt disponible' : undefined}
                            primaryTypographyProps={{
                              fontWeight: isActive ? 600 : 400,
                              fontSize: '0.9rem',
                            }}
                            secondaryTypographyProps={{
                              fontSize: '0.75rem',
                              fontStyle: 'italic',
                            }}
                          />
                        )}
                      </ListItemButton>
                    </span>
                  </Tooltip>
                </ListItem>
              );
            })}
          </List>
        </Collapse>

        {/* Configuration - Collapsible */}
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => setConfigOpen(!configOpen)}
            sx={{
              minHeight: 48,
              justifyContent: desktopOpen ? 'initial' : 'center',
              px: desktopOpen ? 2.5 : 0,
            }}
          >
            <ListItemIcon sx={{
              minWidth: 0,
              mr: desktopOpen ? 3 : 0,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}>
              <ConfigIcon />
            </ListItemIcon>
            {desktopOpen && (
              <>
                <ListItemText
                  primary="Configuration"
                  primaryTypographyProps={{ fontWeight: 600 }}
                />
                {configOpen ? <ExpandLess /> : <ExpandMore />}
              </>
            )}
          </ListItemButton>
        </ListItem>

        <Collapse in={configOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {configItemsWithPermissions.map((item) => {
              const isDisabled = !item.hasAccess;
              const isActive = !isDisabled && isActiveRoute(item.path);
              const tooltipTitle = !item.hasAccess ? 'Accès refusé' : '';

              return (
                <ListItem key={item.text} disablePadding>
                  <Tooltip title={tooltipTitle} placement="right" arrow>
                    <span style={{ width: '100%' }}>
                      <ListItemButton
                        selected={isActive}
                        onClick={!isDisabled ? () => navigate(item.path) : undefined}
                        disabled={isDisabled}
                        sx={{
                          minHeight: 44,
                          justifyContent: desktopOpen ? 'initial' : 'center',
                          pl: desktopOpen ? 4 : 0,
                          pr: desktopOpen ? 2.5 : 0,
                          '&.Mui-selected': {
                            backgroundColor: 'primary.light',
                            color: 'primary.contrastText',
                            '&:hover': {
                              backgroundColor: 'primary.main',
                            },
                          },
                          '&.Mui-disabled': {
                            opacity: 0.6,
                            cursor: 'not-allowed',
                          },
                          cursor: !isDisabled ? 'pointer' : 'not-allowed',
                        }}
                      >
                        <ListItemIcon sx={{
                          minWidth: 0,
                          mr: desktopOpen ? 3 : 0,
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          color: isActive ? 'primary.contrastText' : 'inherit',
                        }}>
                          {item.icon}
                        </ListItemIcon>
                        {desktopOpen && (
                          <ListItemText
                            primary={item.text}
                            primaryTypographyProps={{
                              fontWeight: isActive ? 600 : 400,
                              fontSize: '0.9rem',
                            }}
                          />
                        )}
                      </ListItemButton>
                    </span>
                  </Tooltip>
                </ListItem>
              );
            })}
          </List>
        </Collapse>
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { 
            sm: desktopOpen ? `calc(100% - ${drawerWidth}px)` : `calc(100% - ${collapsedDrawerWidth}px)`
          },
          ml: { 
            sm: desktopOpen ? `${drawerWidth}px` : `${collapsedDrawerWidth}px`
          },
          bgcolor: '#2196F3',
          transition: 'width 0.3s ease, margin 0.3s ease',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <IconButton
            color="inherit"
            aria-label="toggle drawer"
            edge="start"
            onClick={handleDesktopDrawerToggle}
            sx={{ mr: 2, display: { xs: 'none', sm: 'block' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            Departement Approvisionnement
          </Typography>
          <HeaderChatButton />
          <NotificationsBell />
          <ThemeToggle />
        </Toolbar>
      </AppBar>
      
      <Box
        component="nav"
        onMouseEnter={() => {
          if (!desktopOpen) setDesktopOpen(true);
        }}
        sx={{
          width: { sm: desktopOpen ? drawerWidth : collapsedDrawerWidth },
          flexShrink: { sm: 0 },
          transition: 'width 0.3s ease'
        }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth 
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: desktopOpen ? drawerWidth : collapsedDrawerWidth,
              transition: 'width 0.3s ease',
              overflowX: 'hidden',
            },
          }}
          open={desktopOpen}
        >
          {drawer}
        </Drawer>
      </Box>
      
      <Box
        component="main"
        onClick={() => {
          if (desktopOpen) setDesktopOpen(false);
        }}
        sx={{
          flexGrow: 1,
          py: 3,
          px: 2,
          width: {
            sm: desktopOpen ? `calc(100% - ${drawerWidth}px)` : `calc(100% - ${collapsedDrawerWidth}px)`
          },
          ml: {
            sm: 0
          },
          transition: 'width 0.3s ease, margin-left 0.3s ease',
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>
    </Box>
  );
};

export default StockLayout;